import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import MapFiltersSection from './MapFiltersSection';
import './map-filters.scss';
import SliderField from '@components/form/SliderField/SliderField';
import * as CheckboxField from '@components/form/CheckboxField/CheckboxField';
import { Form } from '@components/form/Form/Form';
import { getFilledFieldsObjOnFormSubmit } from '@utils/utils';
import { LoadingComponent } from '@components/LoadingComponent/LoadingComponent';
import { useMapFiltersContext } from '@context/mapFiltersContext';
import { MapFiltersType, TemporaryMapFilterType } from '../../types';

const MIN = 1;
const MAX = 11;
const STEP = 1;
const lanesSliderMarks = Array.from({ length: Math.ceil((MAX - MIN) / STEP) - 1 }, (_, i) => ({
    value: i + 1,
    label: `${i + 1}`,
}));

const MapRoadsFilter = () => {
    const {
        loading,
        setLoading,
        enabledFilters,
        setEnabledFilters,
        filters,
        setFilters,
        resetFilters,
        setMapFilter,
    } = useMapFiltersContext();
    const { t } = useTranslation();

    const lanesDefaultValue = useMemo(() => {
        return filters['lanesNumMin'] && filters['lanesNumMax']
            ? [filters['lanesNumMin'], filters['lanesNumMax']]
            : [1, 10];
    }, [filters]);

    const getMapFilter = useCallback(
        (enabledFilters: MapFiltersType, filters: TemporaryMapFilterType) => {
            const { modes, lanesNumMin, lanesNumMax, highwayTypes, onewayTypes } = filters;

            const _filter: any[] = ['all'];
            if (enabledFilters['modes'].length) {
                const modesFilters = (['any'] as any).concat(
                    enabledFilters['modes']
                        .filter(mode => modes[mode])
                        .map(mode => ['in', mode, ['get', 'modes']])
                );
                _filter.push(modesFilters);
            }
            if (lanesNumMin) {
                _filter.push(['>=', ['get', 'lanes'], lanesNumMin]);
            }
            if (lanesNumMax) {
                _filter.push(['<=', ['get', 'lanes'], lanesNumMax]);
            }
            if (enabledFilters['highwayTypes'].length) {
                const highwayTypesFilter = enabledFilters['highwayTypes'].filter(
                    highwayType => highwayTypes[highwayType]
                );
                _filter.push(['in', ['get', 'highway'], ['literal', highwayTypesFilter]]);
            }
            if (enabledFilters['onewayTypes'].length) {
                const onewayTypesFilter = enabledFilters['onewayTypes'].filter(
                    onewayType => onewayTypes[onewayType]
                );
                _filter.push([
                    'in',
                    ['downcase', ['to-string', ['get', 'oneway']]],
                    ['literal', onewayTypesFilter.map(onewayType => onewayType.toLowerCase())],
                ]);
            }

            return _filter.length > 1 ? _filter : null;
        },
        []
    );

    const onFormSubmit = useCallback(
        async event => {
            const formDataObj = getFilledFieldsObjOnFormSubmit(event);

            const modes = enabledFilters['modes'].reduce((acc, name) => {
                acc[name] = formDataObj[name] === 'true';
                return acc;
            }, {});

            const [lanesNumMin, lanesNumMax] = JSON.parse(formDataObj['lanes'] as string);

            const highwayTypes = enabledFilters['highwayTypes'].reduce((acc, name) => {
                acc[name] = formDataObj[name] === 'true';
                return acc;
            }, {});
            const onewayTypes = enabledFilters['onewayTypes'].reduce((acc, name) => {
                acc[name] = formDataObj[name] === 'true';
                return acc;
            }, {});

            const filters: TemporaryMapFilterType = {
                modes,
                lanesNumMin,
                lanesNumMax,
                highwayTypes,
                onewayTypes,
            };

            const mapFilter = getMapFilter(enabledFilters as MapFiltersType, filters);
            setMapFilter(mapFilter);
            setFilters(filters);
        },
        [setMapFilter, enabledFilters]
    );

    return (
        <div className="map-filters">
            <LoadingComponent isLoading={loading}>
                <div className="map-filters__header">
                    <button onClick={resetFilters}>{t('map-filters.reset-filters-btn')}</button>
                </div>
                <Form
                    name="mapFiltersForm"
                    onSubmit={onFormSubmit}
                    submitBtnTitle={t('map-filters.apply-filters-btn')}
                    testId="mapFiltersForm"
                    submitBtnTestId="mapFiltersFormSubmitBtn"
                >
                    <MapFiltersSection title={t('map-filters.transport-modes-group')}>
                        <div className="map-filters__modes-section">
                            {enabledFilters['modes']?.map((name, idx) => {
                                if (name) {
                                    return (
                                        <CheckboxField.Labeled
                                            key={idx}
                                            inline={true}
                                            name={name}
                                            checked={filters['modes'][name]}
                                            alignCheckboxRight
                                            testId={`${name}_mode_checkbox`}
                                        />
                                    );
                                }
                            })}
                        </div>
                    </MapFiltersSection>
                    <MapFiltersSection title={t('map-filters.lanes-number-group')}>
                        <SliderField
                            name="lanes"
                            min={MIN}
                            max={Math.ceil((MAX - MIN) / STEP)}
                            step={STEP}
                            marks={lanesSliderMarks}
                            defaultValue={lanesDefaultValue}
                            required
                        />
                    </MapFiltersSection>
                    <MapFiltersSection title={t('map-filters.highway-type-group')}>
                        <div className="map-filters__modes-section">
                            {enabledFilters['highwayTypes']?.map((name, idx) => {
                                return (
                                    <CheckboxField.Labeled
                                        key={idx}
                                        inline={true}
                                        name={name}
                                        checked={filters['highwayTypes'][name]}
                                        alignCheckboxRight
                                    />
                                );
                            })}
                        </div>
                    </MapFiltersSection>
                    <MapFiltersSection title={t('map-filters.oneway-type-group')}>
                        <div className="map-filters__modes-section">
                            {enabledFilters['onewayTypes']?.map((name, idx) => {
                                return (
                                    <CheckboxField.Labeled
                                        key={idx}
                                        inline={true}
                                        name={name}
                                        checked={filters['onewayTypes'][name]}
                                        alignCheckboxRight
                                    />
                                );
                            })}
                        </div>
                    </MapFiltersSection>
                </Form>
            </LoadingComponent>
        </div>
    );
};

export default MapRoadsFilter;
